import { AssistantMessageCitedSource } from "#features/conversations/components/conversation-source/AssistantMessageCitedSource.tsx";
import { AssistantMessageUser } from "#features/conversations/components/conversation-user/AssistantMessageUser.tsx";
import { useCitedContentHelpers, type CitedConversation } from "#features/conversations/hooks/useMessageContent.ts";
import { useTracking } from "#hooks/useTracking.ts";
import { MixpanelEvent, MixpanelLinkType, MixpanelSourceLocation } from "#types/tracking.ts";
import type { ConversationMessageModel } from "#features/conversations/types/ConversationMessageModel.ts";
import { Link } from "@hopper-ui/components";
import { useCallback, useMemo } from "react";
import type { Components, ExtraProps } from "react-markdown";

type MarkdownNode = NonNullable<ExtraProps["node"]>["children"][number];

// What a link reads as, whatever formatting wraps it: `[**Jane** Doe](mailto:…)` is "Jane Doe".
const plainText = (nodes: MarkdownNode[] = []): string => nodes
    .map(node => (node.type === "text" ? node.value : "children" in node ? plainText(node.children) : ""))
    .join("");

/**
 * Shared so the message body and the person profile card render one turn's citations identically —
 * same source chips, same member cards, and both withhold their hover card while it streams.
 * `citationNumbers` maps a stored citation index to the number its pill shows (useMessageCitations),
 * computed once per answer by the caller.
 */
export const useCitedMarkdownComponents = (
    cited: CitedConversation,
    message: ConversationMessageModel,
    citationNumbers: ReadonlyMap<number, number>
): Components => {
    const { assistantStatus } = message;
    const { isContentCitedSource, isContentMailto, getParsedSource, extractEmail } = useCitedContentHelpers(cited);
    const { trackEvent } = useTracking();

    const onLinkPress = useCallback(() => {
        trackEvent(MixpanelEvent.AssistantSourceClicked, {
            Type: MixpanelLinkType.Link,
            "Conversation Id": cited.conversationId,
            Location: MixpanelSourceLocation.Inline
        });
    }, [cited.conversationId, trackEvent]);

    return useMemo<Components>(() => ({
        a({ children, href, node }) {
            const disabled = assistantStatus !== "idle";

            if (isContentCitedSource(children?.toString(), href)) {
                const { matchedSource } = getParsedSource(children as string, href);

                if (matchedSource) {
                    return <AssistantMessageCitedSource
                        conversationId={cited.conversationId}
                        index={citationNumbers.get(matchedSource.citationIndex) ?? matchedSource.citationIndex}
                        source={matchedSource}
                        disabled={disabled}
                    />;
                }
            }

            if (isContentMailto(href as string)) {
                const email = extractEmail(href as string);

                return <AssistantMessageUser
                    href={href as string}
                    userName={plainText(node?.children)}
                    email={email}
                    disabled={disabled}
                    conversationId={cited.conversationId}
                />;
            }

            return (
                <Link
                    size="sm"
                    variant="secondary"
                    target="_blank"
                    onPress={onLinkPress}
                    href={href}
                >
                    {children}
                </Link>
            );
        }
    }), [assistantStatus, cited.conversationId, extractEmail, getParsedSource, isContentCitedSource, isContentMailto, citationNumbers, onLinkPress]);
};
